/**
 * The calendar and NOW (1.O-1 deliverable 1), and the module's shared reads. `useWhenContext()`
 * reads `calendar.get` and `clock.get` (only `GET` on load) and compiles the calendar once per
 * version. Both routes answer 200 null while there is none yet (DECISIONS 567): a null calendar is
 * `none`, a null clock `unset`, and any other clock failure `unknown` (so the When input works
 * before 1.L-2's stage 2). `useWhenEvents` keeps the reads fresh from `chrono.changed` and
 * `clock.changed`.
 */
import { type CompiledCalendar, compile } from '@codex/calendar';
import {
  ApiError,
  type CalendarV1,
  type ClockV1,
  calendarGet,
  chronoChangeTouches,
  clockGet,
  flagsFromMe,
  type PlacementV1,
  placementsList,
} from '@codex/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../auth/client.js';
import { calendarKey } from '../calendar/queries.js';
import { appEvents } from '../editor-services/events.js';
import { useShellSession } from '../shell/session.js';

export const whenKeys = {
  /**
   * The calendar page's own entry (1.O-3's `calendarKey`, the same read: null for none yet), so a
   * page shows one `calendar.get` however many readers it has (the NOW chip is on every page) and
   * a commit on `/calendar` reaches the chip at once.
   */
  calendar: calendarKey,
  clock: ['when', 'clock'] as const,
  placements: (docId: string) => ['when', 'placements', docId] as const,
  /** Other pages' placements naming this page as a participant (CS item W1). */
  participating: (docId: string) => ['when', 'participating', docId] as const,
  links: (docId: string) => ['when', 'links', docId] as const,
  /** The pages of the placements a page's rules name, from the Chronicle. */
  owners: (ids: readonly string[]) => ['when', 'owners', ids.join(',')] as const,
  titles: (ids: readonly string[]) => ['when', 'titles', ids.join(',')] as const,
};

export interface WhenContextValue {
  readonly status: 'loading' | 'ready' | 'none' | 'error';
  readonly calendar: CompiledCalendar | null;
  readonly version: number | null;
  readonly now: number | null;
  readonly nowState: 'set' | 'unset' | 'unknown';
}

const is404 = (error: unknown) => error instanceof ApiError && error.status === 404;

let compiled: { version: number; id: string; calendar: CompiledCalendar | null } | null = null;

/** `compile` once per calendar version; null when the definition does not compile. */
export function compiledFor(row: CalendarV1): CompiledCalendar | null {
  if (compiled?.version !== row.version || compiled.id !== row.id) {
    const result = compile(row.definition);
    compiled = { version: row.version, id: row.id, calendar: result.ok ? result.calendar : null };
  }
  return compiled.calendar;
}

/** The `chrono` flag, read only from `auth.me`. */
export function useChronoFlag(): boolean {
  const me = useShellSession();
  return me !== null && flagsFromMe(me).chrono === true;
}

/**
 * `clock.get`: NOW's row, or the null the route answers before the first set (DECISIONS 567, as
 * the calendar's none is null). None yet is data, not an error: a query left in error refetches
 * whenever a reader mounts, and the NOW popover's Set form (a second reader) then unmounted and
 * remounted for ever (DECISIONS 441). Any failure stays an error, and NOW is `unknown`.
 */
export function useClockQuery(enabled = true) {
  return useQuery({
    queryKey: whenKeys.clock,
    queryFn: (): Promise<ClockV1 | null> => api(clockGet),
    retry: false,
    enabled,
  });
}

/** Whether a failure is a 404 `not_found` (another route's missing row; the calendar and NOW answer null). */
export function isNotFound(error: unknown): boolean {
  return is404(error);
}

export function useWhenContext(enabled = true): WhenContextValue {
  const calendarQ = useQuery({
    queryKey: whenKeys.calendar,
    queryFn: (): Promise<CalendarV1 | null> => api(calendarGet),
    retry: false,
    enabled,
  });
  const clockQ = useClockQuery(enabled);
  const presentTick = clockQ.data?.presentTick ?? null;
  // None yet arrives as null data (`useClockQuery`), so only a read that has not answered, or
  // failed, is `unknown`.
  const nowState = clockQ.isSuccess ? (presentTick === null ? 'unset' : 'set') : 'unknown';
  const now = nowState === 'set' ? presentTick : null;
  if (calendarQ.isPending) {
    return { status: 'loading', calendar: null, version: null, now, nowState };
  }
  if (calendarQ.isError) return { status: 'error', calendar: null, version: null, now, nowState };
  if (calendarQ.data === null)
    return { status: 'none', calendar: null, version: null, now, nowState };
  const calendar = compiledFor(calendarQ.data);
  return {
    status: calendar === null ? 'error' : 'ready',
    calendar,
    version: calendarQ.data.version,
    now,
    nowState,
  };
}

export function usePlacements(docId: string | null, enabled = true) {
  return useQuery({
    queryKey: whenKeys.placements(docId ?? ''),
    queryFn: async (): Promise<PlacementV1[]> =>
      (await api(placementsList, { params: { id: docId as string }, query: {} })).items,
    enabled: enabled && docId !== null,
    retry: false,
  });
}

/**
 * One subscription per mounted reader: a `chrono.changed` that touches the page (an empty `docIds`
 * touches every page) refetches its placements, links and rule titles, one at another calendar
 * version refetches the calendar, and every one refetches the events pages take part in (an event
 * names its own page, not its participants); `clock.changed` refetches NOW. With no page (the NOW
 * chip) only the calendar and NOW are kept fresh.
 */
export function useWhenEvents(docId: string | null, enabled = true): void {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const offChrono = appEvents.subscribe('chrono.changed', (event) => {
      if (docId !== null) {
        if (chronoChangeTouches(event, docId)) {
          for (const key of ['placements', 'links', 'owners']) {
            void client.invalidateQueries({ queryKey: ['when', key] });
          }
        }
        void client.invalidateQueries({ queryKey: ['when', 'participating'] });
      }
      const cached = client.getQueryData<CalendarV1 | null>(whenKeys.calendar);
      if (cached !== undefined && cached?.version !== event.data.calendarVersion) {
        void client.invalidateQueries({ queryKey: whenKeys.calendar });
      }
    });
    const offClock = appEvents.subscribe('clock.changed', () => {
      void client.invalidateQueries({ queryKey: whenKeys.clock });
    });
    return () => {
      offChrono();
      offClock();
    };
  }, [client, docId, enabled]);
}
